import type { ApprovalChoice, RunAction, RunControlState } from "@herskin/contracts";
import { Button, InlineError, StatusBadge } from "@herskin/ui";
import { useState } from "react";

const APPROVAL_LABELS: Record<ApprovalChoice, string> = {
  once: "仅本次允许",
  session: "本会话允许",
  always: "以后始终允许",
  deny: "拒绝",
};

function actionState(action: RunAction) {
  if (action.status === "pending") {
    return {
      label: action.type === "approval" ? "等待批准" : "等待补充",
      tone: "warning" as const,
    };
  }
  if (action.status === "submitting") return { label: "正在提交", tone: "warning" as const };
  if (action.status === "unknown") return { label: "结果待确认", tone: "danger" as const };
  if (action.status === "expired") return { label: "已过期", tone: "neutral" as const };
  if (action.status === "cancelled") return { label: "已取消", tone: "neutral" as const };
  if (action.type === "approval") {
    return action.selectedChoice === "deny"
      ? { label: "已拒绝", tone: "neutral" as const }
      : { label: "已批准", tone: "success" as const };
  }
  return { label: "已回答", tone: "success" as const };
}

/**
 * 同一组操作卡同时用于聊天时间线与右侧检查器。审批/澄清不能只藏在侧栏，
 * 否则侧栏关闭或移动端切换后会再次出现“状态等待我，但找不到操作入口”。
 */
export function RunActionCards({
  actions,
  busyActionId,
  mode,
  onApproval,
  onClarification,
}: {
  actions: RunAction[];
  busyActionId: string | null;
  mode: "timeline" | "inspector";
  onApproval: (action: Extract<RunAction, { type: "approval" }>, choice: ApprovalChoice) => void;
  onClarification: (action: Extract<RunAction, { type: "clarification" }>, answer: string) => void;
}) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [selectedChoices, setSelectedChoices] = useState<Record<string, string[]>>({});

  if (!actions.length) return null;

  return (
    <div className={`run-action-stack run-action-stack--${mode}`}>
      {actions.map((action) => {
        const state = actionState(action);
        const pending = action.status === "pending";
        return action.type === "approval" ? (
          <article className="run-action-card run-action-card--approval" key={action.id}>
            <div className="run-action-title">
              <StatusBadge tone={state.tone} showDot>
                {state.label}
              </StatusBadge>
              <strong>{action.description}</strong>
            </div>
            {action.command ? <pre>{action.command}</pre> : null}
            {pending ? (
              <div className="run-action-buttons">
                {action.choices.map((choice) => (
                  <Button
                    key={choice}
                    size="sm"
                    variant={choice === "once" ? "primary" : "secondary"}
                    disabled={busyActionId === action.id}
                    onClick={() => onApproval(action, choice)}
                  >
                    {APPROVAL_LABELS[choice]}
                  </Button>
                ))}
              </div>
            ) : (
              <p className="run-action-outcome">
                {action.status === "resolved" && action.selectedChoice
                  ? `处理结果：${APPROVAL_LABELS[action.selectedChoice]}`
                  : action.status === "unknown"
                    ? "提交结果无法确认，HerSkin 不会自动重试。"
                    : state.label}
              </p>
            )}
          </article>
        ) : (
          <article className="run-action-card" key={action.id}>
            <div className="run-action-title">
              <StatusBadge tone={state.tone} showDot>
                {state.label}
              </StatusBadge>
              <strong>{action.question}</strong>
            </div>
            {pending ? (
              <>
                {action.choices.length ? (
                  <div className="clarification-choices">
                    {action.choices.map((choice) => (
                      <button
                        type="button"
                        key={choice}
                        onClick={() => {
                          const current = selectedChoices[action.id] ?? [];
                          const next = action.multiSelect
                            ? current.includes(choice)
                              ? current.filter((item) => item !== choice)
                              : [...current, choice]
                            : [choice];
                          setSelectedChoices((values) => ({ ...values, [action.id]: next }));
                          setAnswers((values) => ({ ...values, [action.id]: next.join("、") }));
                        }}
                        aria-pressed={(selectedChoices[action.id] ?? []).includes(choice)}
                      >
                        {choice}
                      </button>
                    ))}
                  </div>
                ) : null}
                <textarea
                  rows={3}
                  aria-label="回答 Hermes 的补充问题"
                  value={answers[action.id] ?? ""}
                  onChange={(event) => {
                    setSelectedChoices((current) => ({ ...current, [action.id]: [] }));
                    setAnswers((current) => ({ ...current, [action.id]: event.target.value }));
                  }}
                />
                <Button
                  size="sm"
                  variant="primary"
                  disabled={!answers[action.id]?.trim() || busyActionId === action.id}
                  onClick={() => onClarification(action, answers[action.id] ?? "")}
                >
                  提交回答
                </Button>
              </>
            ) : (
              <p className="run-action-outcome">
                {action.status === "resolved" && action.answer
                  ? `回答：${action.answer}`
                  : action.status === "unknown"
                    ? "提交结果无法确认，HerSkin 不会自动重试。"
                    : state.label}
              </p>
            )}
          </article>
        );
      })}
    </div>
  );
}

export function RunInspector({
  state,
  busyActionId,
  error,
  onApproval,
  onClarification,
  onStop,
}: {
  state: RunControlState | null;
  busyActionId: string | null;
  error: string;
  onApproval: (action: Extract<RunAction, { type: "approval" }>, choice: ApprovalChoice) => void;
  onClarification: (action: Extract<RunAction, { type: "clarification" }>, answer: string) => void;
  onStop: () => void;
}) {
  const actionable =
    state?.actions.filter((action) =>
      ["pending", "submitting", "unknown"].includes(action.status),
    ) ?? [];
  const activities = state?.activities ?? [];

  if (!state?.run && !activities.length && !actionable.length) return null;

  return (
    <section className="inspector-card run-inspector-card">
      <div className="run-inspector-heading">
        <span className="card-label">运行过程</span>
        {state?.canStop ? (
          <Button size="sm" variant="secondary" onClick={onStop}>
            停止当前运行
          </Button>
        ) : null}
      </div>
      {error ? <InlineError title="运行操作没有完成" message={error} /> : null}
      <RunActionCards
        actions={actionable}
        busyActionId={busyActionId}
        mode="inspector"
        onApproval={onApproval}
        onClarification={onClarification}
      />

      {activities.length ? (
        <ol className="run-activity-list">
          {activities.map((activity) => (
            <li key={activity.id}>
              <span className={`run-activity-dot run-activity-dot--${activity.status}`} />
              <div>
                <strong>{activity.title}</strong>
                {activity.detail ? <p>{activity.detail}</p> : null}
                {activity.durationMs !== null ? (
                  <small>{(activity.durationMs / 1_000).toFixed(1)} 秒</small>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      ) : actionable.length ? null : (
        <p>Hermes 已开始运行，等待过程事件。</p>
      )}
    </section>
  );
}
